/**
 * Avatar — Imagem circular com fallback pra iniciais e status indicator
 */
import { useState } from 'preact/hooks';
import { T } from '../../styles/tokens';

interface AvatarProps {
  /** URL da imagem */
  src?: string;
  /** Nome completo (usado pro fallback de iniciais) */
  name: string;
  /** Tamanho */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Status online/offline */
  status?: 'online' | 'offline';
}

const SIZES = { sm: 24, md: 32, lg: 40, xl: 56 } as const;
const STATUS_SIZES = { sm: 8, md: 10, lg: 12, xl: 14 } as const;

/** Gera iniciais a partir do nome */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
}

/** Gera cor de fundo baseada no nome (determinístico) */
function getColor(name: string): string {
  const colors = [T.primary, T.success, T.warning, T.purple, T.danger];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export function Avatar({ src, name, size = 'md', status }: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const px = SIZES[size];
  const statusPx = STATUS_SIZES[size];
  const showImage = src && !imgError;

  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      {showImage ? (
        <img
          src={src}
          alt={name}
          onError={() => setImgError(true)}
          style={{
            width: px,
            height: px,
            borderRadius: T.radiusFull,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <div style={{
          width: px,
          height: px,
          borderRadius: T.radiusFull,
          background: getColor(name),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: T.text,
          fontSize: px * 0.4,
          fontWeight: T.weightSemibold,
          userSelect: 'none',
        }}>
          {getInitials(name)}
        </div>
      )}
      {/* Status indicator */}
      {status && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: statusPx,
          height: statusPx,
          borderRadius: T.radiusFull,
          background: status === 'online' ? T.success : T.textMuted,
          border: `2px solid ${T.bg}`,
        }} />
      )}
    </div>
  );
}
